/**
 * @module Key and input handling for the Tiptap composer.
 *
 * Ported from t3code's composer rich-text layer (ticket 3526, upstream pin
 * e3e7cc3f, MIT license retained in LICENSE), merged with Forge's surround
 * selection and command-key contract. Pure over the ProseMirror view so the
 * component stays presentational.
 */

import { splitBlockKeepMarks } from "@tiptap/pm/commands";
import type { EditorView } from "@tiptap/pm/view";
import { TextSelection } from "@tiptap/pm/state";

import { collapsedToFlat, flatToCollapsed, flatToPm } from "~/composer-rich-text-cursor";

import { selectionTouchesMentionBoundary } from "~/composer-editor-mentions";
import { serializeEditorDoc } from "~/composer-rich-text-serialize";
import { flatToMarkdown, pmToFlat } from "~/composer-rich-text-cursor";
import { isMacPlatform } from "~/lib/utils";

export type ComposerCommandKey = "ArrowDown" | "ArrowUp" | "Enter" | "Tab";

const SURROUND_CLOSE: Record<string, string> = {
  "(": ")",
  "[": "]",
  "{": "}",
  "'": "'",
  '"': '"',
  "“": "”",
  "`": "`",
  "<": ">",
  "«": "»",
  "*": "*",
  _: "_",
};

export interface ComposerKeyDownHandlers {
  readonly onCommandKeyDown?:
    | ((key: ComposerCommandKey, event: KeyboardEvent, editorValue: string) => boolean)
    | undefined;
  /** Notified when a Backspace deletes a terminal-context chip. */
  readonly onRemoveTerminalContext: (contextId: string) => void;
  readonly richText: boolean;
  readonly isActive: (name: string) => boolean;
  readonly splitTaskItem: () => boolean;
  readonly liftEmptyTaskItem: () => boolean;
}

/**
 * Backspace at a chip removes the whole chip as one cursor stop. A
 * terminal-context chip also notifies the owning composer state (the store
 * drops the draft and its prompt placeholder); its caret keeps its collapsed
 * offset instead of hopping left, so a follow-up Backspace deletes the space
 * the user sees.
 */
function handleBackspaceAtChip(view: EditorView, handlers: ComposerKeyDownHandlers): boolean {
  const { $from } = view.state.selection;
  if (!view.state.selection.empty) return false;
  const before = $from.nodeBefore;
  if (!before || !before.type.name.startsWith("composer-")) return false;
  const isTerminalContext = before.type.name === "composer-terminal-context";
  const caretCollapsed = isTerminalContext
    ? flatToCollapsed(
        serializeEditorDoc(view.state.doc),
        pmToFlat(serializeEditorDoc(view.state.doc), $from.pos),
      )
    : null;
  view.dispatch(view.state.tr.delete($from.pos - before.nodeSize, $from.pos));
  if (isTerminalContext) {
    const context = (before.attrs as { context?: { id?: string } | null }).context;
    if (context?.id) handlers.onRemoveTerminalContext(context.id);
    const map = serializeEditorDoc(view.state.doc);
    const flat = collapsedToFlat(map, caretCollapsed ?? 0);
    view.dispatch(
      view.state.tr
        .setSelection(TextSelection.create(view.state.doc, flatToPm(map, flat)))
        .scrollIntoView(),
    );
  } else {
    view.dispatch(view.state.tr.scrollIntoView());
  }
  return true;
}

/**
 * Mac Home/End move to the visual line boundary; ProseMirror would move to
 * the document boundary, so the native text edit is replayed onto the doc.
 */
// fallow-ignore-next-line complexity -- ported upstream t3code algorithm (ticket 3526); behavior pinned by the ported spec
function handleMacLineBoundary(view: EditorView, event: KeyboardEvent): boolean {
  if (
    !isMacPlatform(navigator.platform) ||
    (event.key !== "Home" && event.key !== "End") ||
    event.altKey ||
    event.metaKey ||
    event.ctrlKey ||
    event.isComposing
  ) {
    return false;
  }
  const selection = window.getSelection();
  if (
    !selection?.anchorNode ||
    !view.dom.contains(selection.anchorNode) ||
    typeof selection.modify !== "function"
  ) {
    return false;
  }
  event.preventDefault();
  event.stopPropagation();
  selection.modify(
    event.shiftKey ? "extend" : "move",
    event.key === "Home" ? "backward" : "forward",
    "lineboundary",
  );
  const anchorNode = selection.anchorNode;
  const focusNode = selection.focusNode;
  if (anchorNode && focusNode) {
    replaySelectionOntoDoc(view, selection, anchorNode, focusNode);
  }
  return true;
}

function replaySelectionOntoDoc(
  view: EditorView,
  selection: Selection,
  anchorNode: Node,
  focusNode: Node,
): void {
  view.dispatch(
    view.state.tr
      .setSelection(
        TextSelection.create(
          view.state.doc,
          view.posAtDOM(anchorNode, selection.anchorOffset),
          view.posAtDOM(focusNode, selection.focusOffset),
        ),
      )
      .scrollIntoView(),
  );
}

/** Arrows hop over chip atoms instead of stopping on their edges. */
// fallow-ignore-next-line complexity -- ported upstream t3code algorithm (ticket 3526); behavior pinned by the ported spec
function handleArrowAtomHop(view: EditorView, event: KeyboardEvent): boolean {
  if (
    (event.key !== "ArrowLeft" && event.key !== "ArrowRight") ||
    event.shiftKey ||
    event.altKey ||
    event.metaKey ||
    event.ctrlKey ||
    event.isComposing ||
    !view.state.selection.empty
  ) {
    return false;
  }
  const { $from } = view.state.selection;
  const direction = event.key === "ArrowLeft" ? -1 : 1;
  const adjacent = direction === -1 ? $from.nodeBefore : $from.nodeAfter;
  if (!adjacent?.type.name.startsWith("composer-")) return false;
  event.preventDefault();
  event.stopPropagation();
  view.dispatch(
    view.state.tr
      .setSelection(TextSelection.create(view.state.doc, $from.pos + direction * adjacent.nodeSize))
      .scrollIntoView(),
  );
  return true;
}

// fallow-ignore-next-line complexity -- ported upstream t3code algorithm (ticket 3526); behavior pinned by the ported spec
export function handleComposerKeyDown(
  view: EditorView,
  event: KeyboardEvent,
  handlers: ComposerKeyDownHandlers,
): boolean {
  if (handleMacLineBoundary(view, event)) return true;
  if (handleArrowAtomHop(view, event)) return true;
  if (event.key === "Backspace" && !event.altKey && !event.metaKey && !event.ctrlKey) {
    if (handleBackspaceAtChip(view, handlers)) {
      event.preventDefault();
      event.stopPropagation();
      return true;
    }
  }

  // IME composition: the Enter that commits the composition must not send.
  if (event.key === "Enter" && (event.isComposing || event.keyCode === 229)) {
    event.stopPropagation();
    return true;
  }
  // Enter on a focused task checkbox must not send the prompt (or split
  // anything): Space toggles it, Enter does nothing.
  if (event.key === "Enter" && event.target instanceof HTMLInputElement) {
    event.preventDefault();
    return true;
  }

  if (event.key === "Enter") {
    return handleEnterKey(view, event, handlers);
  }
  const handler = handlers.onCommandKeyDown;
  if (!handler) return false;
  const key =
    event.key === "Tab"
      ? ("Tab" as const)
      : event.key === "ArrowDown"
        ? ("ArrowDown" as const)
        : event.key === "ArrowUp"
          ? ("ArrowUp" as const)
          : null;
  if (!key) return false;
  const handled = handler(key, event, serializeEditorDoc(view.state.doc).value);
  if (handled) {
    event.preventDefault();
    event.stopPropagation();
  }
  return handled;
}

/** Enter routes to the command handler first, then splits the paragraph. */
function handleEnterKey(
  view: EditorView,
  event: KeyboardEvent,
  handlers: ComposerKeyDownHandlers,
): boolean {
  const handler = handlers.onCommandKeyDown;
  const isTaskItem = handlers.richText && handlers.isActive("taskItem");
  const editorValue = serializeEditorDoc(view.state.doc).value;
  const handled = handler?.("Enter", event, editorValue) ?? false;
  if (handled) {
    event.preventDefault();
    event.stopPropagation();
    return true;
  }
  event.preventDefault();
  if (
    isTaskItem &&
    (handlers.splitTaskItem() ||
      (view.state.selection.$from.parent.content.size === 0 && handlers.liftEmptyTaskItem()))
  ) {
    return true;
  }
  // Split the paragraph so a single newline visibly advances the caret.
  return splitBlockKeepMarks(view.state, (tr) => {
    view.dispatch(tr.scrollIntoView());
  });
}

/** Typing an opener with a selection wraps the selection in the pair. */
export function handleComposerTextInput(
  view: EditorView,
  from: number,
  to: number,
  text: string,
): boolean {
  if (text.length !== 1) return false;
  const closer = SURROUND_CLOSE[text];
  if (!closer || from === to) return false;
  // Never wrap chips or other atoms, and never wrap styled text: the default
  // replace keeps marks intact, wrapping would drop them.
  let touchesSpecial = false;
  view.state.doc.nodesBetween(from, to, (node) => {
    if ((node.isAtom && node.isInline && !node.isText) || (node.isText && node.marks.length > 0)) {
      touchesSpecial = true;
      return false;
    }
    return true;
  });
  if (touchesSpecial) return false;
  const map = serializeEditorDoc(view.state.doc);
  const startMd = flatToMarkdown(map, pmToFlat(map, from));
  const endMd = flatToMarkdown(map, pmToFlat(map, to));
  if (selectionTouchesMentionBoundary(map.value, startMd, endMd)) return false;
  const tr = view.state.tr.insertText(closer, to).insertText(text, from);
  tr.setSelection(TextSelection.create(tr.doc, from + text.length, to + text.length));
  view.dispatch(tr);
  return true;
}

/**
 * Client rect of the caret's visual line, so prompt history keeps claiming
 * ArrowUp/Down only at the first and last soft-wrapped lines.
 */
function paintedRect(rect: DOMRect): DOMRect | null {
  return rect.height > 0 ? rect : null;
}

function firstPaintedRect(range: Range): DOMRect | null {
  return Array.from(range.getClientRects()).find((rect) => rect.height > 0) ?? null;
}

function probeCharacterRect(node: Text, offset: number): DOMRect | null {
  const probeStart = Math.max(0, Math.min(offset, node.data.length - 1));
  const probeRange = document.createRange();
  probeRange.setStart(node, probeStart);
  probeRange.setEnd(node, probeStart + 1);
  return firstPaintedRect(probeRange) ?? paintedRect(probeRange.getBoundingClientRect());
}

// fallow-ignore-next-line complexity -- ported upstream t3code algorithm (ticket 3526); behavior pinned by the ported spec
function caretLineRect(range: Range, edge: "start" | "end"): DOMRect | null {
  const collapsedRects = Array.from(range.getClientRects()).filter((rect) => rect.height > 0);
  const collapsedRect = edge === "start" ? collapsedRects.at(-1) : collapsedRects[0];
  if (collapsedRect) return collapsedRect;

  const container = range.startContainer;
  // TEXT_NODE without importing the DOM lib's Node (shadowed by Tiptap's).
  if (container.nodeType === 3) {
    const textNode = container as Text;
    if (textNode.data.length === 0) return null;
    const caretOffset = edge === "start" ? range.startOffset : range.startOffset - 1;
    return probeCharacterRect(textNode, caretOffset);
  }

  if (!(container instanceof HTMLElement)) return null;
  const neighbour =
    container.childNodes[Math.max(0, range.startOffset - 1)] ??
    container.childNodes[range.startOffset];
  if (neighbour instanceof HTMLElement) {
    return paintedRect(neighbour.getBoundingClientRect());
  }
  if (neighbour instanceof Text && neighbour.data.length > 0) {
    const isBeforeCaret = neighbour === container.childNodes[range.startOffset - 1];
    return probeCharacterRect(neighbour, isBeforeCaret ? neighbour.data.length - 1 : 0);
  }
  return paintedRect(container.getBoundingClientRect());
}

/**
 * True when the collapsed caret sits on the first ("start") or last ("end")
 * visual line, counting soft wraps. Kept next to its rect helper.
 */
export function isCaretOnVisualEdge(
  snapshot: { value: string; expandedCursor: number },
  editorDom: HTMLElement | null | undefined,
  edge: "start" | "end",
): boolean {
  if (snapshot.value.length === 0) return true;
  const beforeCaret = snapshot.value.slice(0, snapshot.expandedCursor);
  const afterCaret = snapshot.value.slice(snapshot.expandedCursor);
  if (edge === "start" ? beforeCaret.includes("\n") : afterCaret.includes("\n")) {
    return false;
  }
  const selection = window.getSelection();
  if (
    !editorDom ||
    !selection ||
    !selection.isCollapsed ||
    selection.rangeCount === 0 ||
    !selection.anchorNode ||
    !editorDom.contains(selection.anchorNode)
  ) {
    return false;
  }
  const caretRect = caretLineRect(selection.getRangeAt(0), edge);
  if (!caretRect) return false;
  const edgeElement = edge === "start" ? editorDom.firstElementChild : editorDom.lastElementChild;
  const edgeRect = (edgeElement ?? editorDom).getBoundingClientRect();
  const threshold = caretRect.height / 2;
  return edge === "start"
    ? caretRect.top - edgeRect.top < threshold
    : edgeRect.bottom - caretRect.bottom < threshold;
}
